import {
    useState,
    useEffect,
    useContext,
    useCallback,
    useRef,
    memo,
} from "react";
import moment from "moment";
import StarekContext from "../../context/StarekContext";
import Spinner from "../../components/Spinner";
import { getSignalTypeFromBits } from "../../utils";
import DeviceMonitorControl, {
    DeviceMonitorControlWidget,
} from "./DeviceMonitorControl";
import DeviceGraphView from "./DeviceGraphView";
import DeviceGraphStatus from "./DeviceGraphStatus";
import {
    fetchAvailableDeviceModels,
    flattenManifestSignalsToTypeArray,
    FALLBACK_SIGNAL_COLORS,
} from "../deviceProfile/AddDeviceProfileWizard";
import {
    compareSignalKeys,
    computePhysicalSignalColors,
    FALLBACK_DEVICE_FLAGS,
} from "./gpioTypeUtils";

// These now have dedicated icon dialogs on the Devices grid (WiFi/battery), so
// they're hidden from the general per-device signal panel to avoid duplication.
const EXCLUDED_SIGNALS = ["/virt/wifi_rssi", "/virt/pwr"];

function DeviceGraph({
    device,
    isExpanded = false,
    sendDeviceCommand,
    deviceProfiles = [],
    onOpenInterpretations,
}) {
    const [currentSignalIndex, setCurrentSignalIndex] = useState(0);
    const [graphData, setGraphData] = useState([]);
    const [initialLoading, setInitialLoading] = useState(true);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [noData, setNoData] = useState(false);
    const [startTime, setStartTime] = useState(moment().subtract(5, "minute"));
    const [endTime, setEndTime] = useState(moment());
    const [mergedSignalData, setMergedSignalData] = useState({});
    const [signalTypeInfo, setSignalTypeInfo] = useState({});
    // Per-address type fallback, same tier-2/tier-3 precedence used
    // everywhere else in this feature (profile config, else stock model) -
    // Device.overrides.signals only ever gets an entry for a signal once
    // its type has actually been overridden at some point, so without this
    // fallback every signal that's never had an override (i.e. most of
    // them) reads type: null here and silently renders no type badges at
    // all, not just "no override applied yet".
    const [manifestTypeByAddress, setManifestTypeByAddress] = useState({});
    // Auto-refresh is on by default - disabling it stops the interval
    // entirely, leaving the graph frozen until the user hits the manual
    // Refresh button (see the toolbar row above the signal nav header).
    const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(true);
    const [refreshIntervalSec, setRefreshIntervalSec] = useState(60);
    // Physical signals are optional to show, off by default; interpreted
    // signals are the business-relevant ones and are always shown - no
    // checkbox to hide them.
    const [showPhysicalSignals, setShowPhysicalSignals] = useState(false);

    // Track the device ID to prevent unnecessary re-fetches when device object reference changes
    const deviceIdRef = useRef(device.id);
    const isExpandedRef = useRef(isExpanded);
    const isMountedRef = useRef(false);

    const starekContext = useContext(StarekContext);
    const { scheme, host, port } = starekContext.lastUsedWorkspace.engine || {};
    const workspace = starekContext.lastUsedWorkspace.name;
    const authToken = starekContext.auth;
    const types = starekContext.types;

    const apiUrl = `${scheme}://${host}:${port}/api/v1/${workspace}/datapoints`;

    useEffect(() => {
        let cancelled = false;
        const deviceFlags =
            starekContext?.types?.device?.flags || FALLBACK_DEVICE_FLAGS;
        const profileId = device?.device_profile?.id;
        if (profileId) {
            const profile = deviceProfiles.find(
                (p) => String(p.id) === String(profileId),
            );
            const entries = flattenManifestSignalsToTypeArray(
                profile?.configuration?.signals,
                deviceFlags,
            );
            setManifestTypeByAddress(
                entries.reduce((acc, e) => ({ ...acc, [e.name]: e.type }), {}),
            );
            return;
        }
        if (!device?.device?.model) {
            setManifestTypeByAddress({});
            return;
        }
        fetchAvailableDeviceModels(starekContext)
            .then((models) => {
                if (cancelled) return;
                const model = models.find((m) => m.code === device.device.model);
                const entries = flattenManifestSignalsToTypeArray(
                    model?.downlink?.signals,
                    deviceFlags,
                );
                setManifestTypeByAddress(
                    entries.reduce((acc, e) => ({ ...acc, [e.name]: e.type }), {}),
                );
            })
            .catch(() => {
                if (!cancelled) setManifestTypeByAddress({});
            });
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [device?.id, device?.device_profile?.id, device?.device?.model, deviceProfiles]);

    // Helper function to send set-signal command - firmware resolves the
    // target purely from `signal` (see app.cpp's set-signal handler), so
    // it must always be included, not just the value being set to.
    const sendSetSignalCommand = useCallback(
        async (signalKey, value) => {
            if (!sendDeviceCommand) {
                console.warn("sendDeviceCommand function not provided");
                return;
            }

            try {
                await sendDeviceCommand({
                    deviceId: device.id,
                    command: "set-signal",
                    parameters: {
                        signal: signalKey,
                        to: value,
                    },
                });
            } catch (error) {
                console.error("Failed to send set-signal command:", error);
                starekContext.showError?.(
                    `Failed to set signal: ${error.message}`
                );
            }
        },
        [device.id, sendDeviceCommand, starekContext]
    );

    // Dynamic height based on expanded state - make it much larger when expanded
    const containerHeight = isExpanded ? "400px" : "120px";
    const chartMargin = isExpanded
        ? { top: 20, right: 20, left: 20, bottom: 20 }
        : { top: 10, right: 10, left: 10, bottom: 30 };

    const fetchDeviceData = useCallback(
        async (isInitial = false) => {
            if (isInitial) {
                setInitialLoading(true);
            } else {
                setIsRefreshing(true);
            }

            // Get current time in UTC
            const nowUtc = moment().utc();
            const startTimeUtc = nowUtc.clone().subtract(15, "minute");

            // Store as local time for display purposes
            setStartTime(moment.unix(startTimeUtc.unix()));
            setEndTime(moment.unix(nowUtc.unix()));

            const requestBody = {
                devices: [device.id],
                start_time: startTimeUtc.unix(),
                end_time: nowUtc.unix(),
                group_by_device: true,
                group_by_signal: true,
            };

            try {
                const response = await fetch(apiUrl, {
                    method: "POST",
                    headers: {
                        Authorization: `Bearer ${authToken}`,
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify(requestBody),
                });

                if (!response.ok)
                    throw new Error(`HTTP error! Status: ${response.status}`);

                const jsonData = await response.json();
                const datapointSignals = jsonData?.data?.[device.id] || {};
                // overrides.signals is an array of {name, type} for any
                // device that's gone through assignDeviceProfile/
                // updateDeviceSignalType, but a device that predates both
                // (raw legacy data) can still have it stored as an object
                // keyed by name - normalize both shapes rather than
                // assuming array, or a legacy device crashes this panel.
                const rawRuntimeSignals = device.device.runtime.signals;
                const runtimeSignals = Array.isArray(rawRuntimeSignals)
                    ? rawRuntimeSignals
                    : rawRuntimeSignals && typeof rawRuntimeSignals === "object"
                      ? Object.entries(rawRuntimeSignals).map(([name, val]) => ({
                            name,
                            ...val,
                        }))
                      : [];

                const parameterSignals =
                    device.parameters || device.device?.parameters || {};
                // Interpretations' own cosmetics (inverted/auxillary/color)
                // live inside parameters.interpretations[], not as a
                // top-level parameters[name] key like a physical signal's
                // do - look them up by name here rather than assuming
                // every key in `parameterSignals` is itself a signal
                // address (interpretations is one, but isn't a signal).
                const interpretationsByName = {};
                (parameterSignals.interpretations || []).forEach((i) => {
                    if (i?.name) interpretationsByName[i.name] = i;
                });

                // Normalize runtimeSignals array to object for efficient merging
                const runtimeSignalsMap = {};
                runtimeSignals.forEach((signal) => {
                    if (signal.name) {
                        runtimeSignalsMap[signal.name] = signal;
                    }
                });

                // The live-authored interpretations list - same source the
                // sidebar's Interpretation tab reads (ShadowDevice.parameters,
                // via computeEffectiveInterpretations) - is the only
                // authoritative answer to "does this interpretation still
                // exist". Device.reported.signals (what runtimeSignalsMap is
                // built from) is the device's own last-latched echo, which
                // can still carry a deleted/renamed interpretation's analyzer
                // for a while after the fact, until the device actually
                // re-latches - without cross-checking this list, a deleted
                // interpretation kept showing up here (with real historical
                // datapoints) even after it was gone from the sidebar.
                const currentInterpretationNames = new Set(
                    (device.device?.runtime?.interpretations || []).map(
                        (i) => i.name,
                    ),
                );

                // A physical signal never carries an analyzer; an
                // interpretation always does (see the plan) - but only
                // trust that for keys not already settled by the
                // authoritative list above, so a brand new interpretation
                // (no reported/datapoint entry yet) is still correctly
                // classified before its first re-latch.
                const isPhysicalKey = (key) =>
                    !currentInterpretationNames.has(key) &&
                    runtimeSignalsMap[key]?.analyzer === undefined;

                // A stale reported-only interpretation - carries an analyzer
                // but isn't in the current authored list anymore - is a
                // ghost from before the device's last re-latch and gets
                // dropped entirely, not just reclassified.
                const isStaleInterpretation = (key) =>
                    !currentInterpretationNames.has(key) &&
                    runtimeSignalsMap[key]?.analyzer !== undefined;

                // Interpretations first (the business-relevant, always-shown
                // channels), physical signals after (optional, off by
                // default) - within each group, the same deterministic
                // bus/port sort the sidebar's Signals tab uses, so ordering
                // never drifts out of sync just because the underlying
                // sources (a DynamoDB List merge, a MySQL JSON column, the
                // datapoints API response) don't guarantee key order.
                // currentInterpretationNames is unioned in directly too, so
                // a freshly-created interpretation with no reported/
                // datapoint entry yet still shows up right away.
                const allKeys = Array.from(
                    new Set(
                        [
                            ...Object.keys(runtimeSignalsMap),
                            ...Object.keys(datapointSignals),
                            ...Object.keys(parameterSignals).filter(
                                (key) => key !== "interpretations",
                            ),
                            ...currentInterpretationNames,
                        ].filter((key) => !EXCLUDED_SIGNALS.includes(key)),
                    ),
                )
                    .filter((key) => !isStaleInterpretation(key))
                    .sort((a, b) => {
                        const aPhys = isPhysicalKey(a) ? 1 : 0;
                        const bPhys = isPhysicalKey(b) ? 1 : 0;
                        if (aPhys !== bPhys) return aPhys - bPhys;
                        return compareSignalKeys(a, b);
                    });

                const signalColors =
                    starekContext?.types?.device?.defaultSignalColors ||
                    FALLBACK_SIGNAL_COLORS;

                // Physical signals carry no stored color of their own
                // anymore (cosmetics moved to Interpretations) - their
                // display color is always this computed BBROYGBVGW
                // sequence, keyed by address so an interpretation reading
                // from one can inherit the same color by default. Computed
                // from `allKeys` (already physical-only when filtered),
                // which preserves compareSignalKeys order within the group.
                const physicalColorByKey = computePhysicalSignalColors(
                    allKeys.filter(isPhysicalKey),
                    signalColors,
                );

                const combinedData = {};

                allKeys.forEach((key) => {
                    const runtime = runtimeSignalsMap[key] || {};
                    const datapoints = datapointSignals?.[key] || [];
                    const parameters =
                        parameterSignals?.[key] ||
                        interpretationsByName[key] ||
                        {};
                    const isPhysical = isPhysicalKey(key);

                    // Physical: always the computed sequential color.
                    // Interpretation: its own explicit color (an override
                    // the user picked) if set, else inherit its source
                    // signal's computed color - the create/edit forms only
                    // persist `color` at all when it differs from the
                    // source's color at save time, so an un-overridden
                    // interpretation genuinely tracks its source's color
                    // going forward rather than a frozen snapshot of it.
                    const color = isPhysical
                        ? `#${physicalColorByKey[key]}`
                        : parameters.color
                          ? `#${parameters.color}`
                          : `#${
                                physicalColorByKey[parameters.source] ||
                                signalColors[0]
                            }`;

                    combinedData[key] = {
                        // Prefer an actual override, but fall back to
                        // whatever the assigned profile/stock model
                        // declares for this address - overrides.signals
                        // only ever has an entry once a type's been
                        // explicitly overridden, so without this fallback
                        // every never-overridden signal reads type: null.
                        type: runtime.type ?? manifestTypeByAddress[key] ?? null,
                        color: color,
                        datapoints,
                        inverted: !!parameters.inverted,
                        auxillary: !!parameters.auxillary,
                        isPhysical,
                    };
                });

                setNoData(datapointSignals && datapointSignals.length === 0);
                setMergedSignalData(combinedData);
            } catch (error) {
                console.error("Failed to fetch device data:", error);
                setNoData(true);
            } finally {
                if (isInitial) {
                    setInitialLoading(false);
                } else {
                    setIsRefreshing(false);
                }
            }
        },
        [apiUrl, authToken, device.id, manifestTypeByAddress]
    );

    useEffect(() => {
        // On first mount, always fetch with initial loading
        if (!isMountedRef.current) {
            isMountedRef.current = true;
            deviceIdRef.current = device.id;
            isExpandedRef.current = isExpanded;
            fetchDeviceData(true);
        } else {
            // After mount, check if device ID or isExpanded actually changed
            const deviceIdChanged = deviceIdRef.current !== device.id;
            const isExpandedChanged = isExpandedRef.current !== isExpanded;

            if (deviceIdChanged) {
                // Device changed, do initial load with spinner
                deviceIdRef.current = device.id;
                isExpandedRef.current = isExpanded;
                fetchDeviceData(true);
            } else if (isExpandedChanged) {
                // Only expanded state changed, refresh without initial spinner
                isExpandedRef.current = isExpanded;
                if (isExpanded) {
                    fetchDeviceData(false);
                }
            }
            // If neither changed, this is just a re-render due to device object reference change - do nothing
        }

        // Only auto-poll when expanded AND the user hasn't turned it off -
        // when disabled, the graph only updates via the manual Refresh
        // button (see the toolbar row above the signal nav header).
        if (isExpanded && autoRefreshEnabled) {
            const interval = setInterval(() => {
                fetchDeviceData(false);
            }, refreshIntervalSec * 1000);
            return () => clearInterval(interval);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [device.id, isExpanded, autoRefreshEnabled, refreshIntervalSec]); // fetchDeviceData intentionally omitted to prevent re-running on device object changes

    // manifestTypeByAddress resolves asynchronously and separately from the
    // effect above - without this, the very first paint would keep every
    // never-overridden signal's type at null until the next 30s auto-
    // refresh tick happened to fire, rather than as soon as the fallback
    // map is actually ready.
    useEffect(() => {
        if (!isMountedRef.current) return;
        fetchDeviceData(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [manifestTypeByAddress]);

    const signalKeys = Object.keys(mergedSignalData).filter((key) => {
        const isPhysical = mergedSignalData[key]?.isPhysical;
        return isPhysical ? showPhysicalSignals : true;
    });
    const currentSignalKey = signalKeys[currentSignalIndex] || "";
    const currentSignal = mergedSignalData[currentSignalKey];

    // Toggling either checkbox can shrink the filtered list out from under
    // whatever index was selected - clamp back into range rather than
    // showing a blank/undefined signal.
    useEffect(() => {
        if (currentSignalIndex > 0 && currentSignalIndex >= signalKeys.length) {
            setCurrentSignalIndex(Math.max(0, signalKeys.length - 1));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [signalKeys.length]);

    // Whether signalTypeInfo (and therefore whether a control widget applies
    // at all) has been resolved for the currently-selected signal - drives
    // the LiveView column reserving its width immediately with a spinner,
    // instead of rendering full-width-then-narrowing once the async decode
    // finishes (see hasControlWidget/showControlColumn below).
    const [signalTypeInfoLoading, setSignalTypeInfoLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        const updateSignalType = async () => {
            if (currentSignal?.type && types) {
                setSignalTypeInfoLoading(true);
                try {
                    const info = await getSignalTypeFromBits(
                        currentSignal.type,
                        types
                    );
                    if (!cancelled) setSignalTypeInfo(info);
                } catch (err) {
                    console.error("Failed to decode signal bits:", err);
                    if (!cancelled) setSignalTypeInfo({});
                } finally {
                    if (!cancelled) setSignalTypeInfoLoading(false);
                }
            } else {
                setSignalTypeInfo({});
                // No type to decode at all (not "still loading") when
                // `types` itself is already available - only genuinely
                // pending while waiting on the /_types fetch.
                setSignalTypeInfoLoading(!types);
            }
        };

        updateSignalType();
        return () => {
            cancelled = true;
        };
    }, [currentSignal?.type, types]);

    useEffect(() => {
        if (!currentSignal) return;
        const isInverted = !!currentSignal.inverted;
        const formatted = currentSignal.datapoints.map((point) => {
            const raw = point.measure_value || 0;
            // "Inverted" describes wiring/logic (e.g. a NC relay reading
            // 1023 for physically OFF), not a change to what the device
            // actually put on the wire - the graph's %/ON-OFF value should
            // read as the real-world state, but Raw stays the true reported
            // value so it never contradicts what the device sent.
            const effective = isInverted ? 1023 - raw : raw;
            return {
                time: point.time,
                value: (effective / 1023) * 100.0,
                measure_value: raw,
            };
        });

        if (
            graphData.length !== formatted.length ||
            JSON.stringify(graphData) !== JSON.stringify(formatted)
        ) {
            setGraphData(formatted);
        }
    }, [currentSignal]);

    // Get initial value for control from latest data point (use measure_value for raw value)
    const initialControlValue =
        graphData.length > 0
            ? graphData[graphData.length - 1]?.measure_value || 0
            : 0;

    const prevSignal = () => {
        if (currentSignalIndex > 0) setCurrentSignalIndex((prev) => prev - 1);
    };

    const nextSignal = () => {
        if (currentSignalIndex < signalKeys.length - 1)
            setCurrentSignalIndex((prev) => prev + 1);
    };

    const lineType = (() => {
        if (signalTypeInfo.digital === true && signalTypeInfo.input === true)
            return "stepAfter";
        if (signalTypeInfo.digital === true && signalTypeInfo.output === true)
            return "stepAfter";
        if (signalTypeInfo.analog === true && signalTypeInfo.input === true)
            return "linear";
        if (signalTypeInfo.analog === true && signalTypeInfo.output === true)
            return "linear";
        return "linear";
    })();

    const CustomTooltip = ({ active, payload }) => {
        if (active && payload?.length) {
            const dataPoint = payload[0].payload;
            const rawValue = dataPoint.measure_value || 0;
            return (
                <div className="tw-bg-white tw-p-3 tw-border tw-rounded tw-shadow-lg">
                    <p
                        className={`${
                            isExpanded ? "tw-text-base" : "tw-text-sm"
                        } tw-font-medium`}
                    >
                        {`Value: ${payload[0].value.toFixed(1)}%`}
                    </p>
                    <p
                        className={`${
                            isExpanded ? "tw-text-sm" : "tw-text-xs"
                        } tw-text-gray-600`}
                    >
                        {`Raw: ${rawValue}`}
                    </p>
                    <p
                        className={`${
                            isExpanded ? "tw-text-sm" : "tw-text-xs"
                        } tw-text-gray-600`}
                    >
                        {`Time: ${moment
                            .unix(payload[0].payload.time)
                            .local()
                            .format("HH:mm:ss")}`}
                    </p>
                </div>
            );
        }
        return null;
    };

    const handleValueChange = (value) => {
        sendSetSignalCommand(currentSignalKey, value);
    };

    // Whether there's actually a control widget to show for the current
    // signal - drives whether the graph gets the full width or shares it
    // with the widget in a right-hand column. An interpretation always gets
    // one (a latest-reading readout) even though it carries no analog/
    // digital/sense/actuate bits of its own (type is a fixed BUS_VIRT
    // placeholder - see the backend's flattenInterpretationsToOverrideEntries)
    // - there's no "actuate an interpretation" concept in this model, only
    // "read its decoded value", so it's always at least Sense-shaped.
    const hasControlWidget =
        isExpanded &&
        (!currentSignal?.isPhysical ||
            (signalTypeInfo.analog &&
                (signalTypeInfo.output || signalTypeInfo.input)) ||
            (signalTypeInfo.digital &&
                (signalTypeInfo.output || signalTypeInfo.input)));
    // Reserve the column (with a spinner) as soon as we know a signal is
    // selected but haven't decoded its type yet, instead of rendering the
    // graph full-width and narrowing it once the widget shows up a moment
    // later.
    const showControlColumn =
        isExpanded && (signalTypeInfoLoading || hasControlWidget);

    // Read straight off the device prop (the same live-authored list the
    // sidebar's Interpretation tab and the ghost-filtering above use), not
    // anything derived from the datapoints fetch - this needs to stay true
    // regardless of whether "Physical signals" is checked, so the banner
    // below has to be independent of signalKeys/showPhysicalSignals.
    const hasNoInterpretations =
        !(device.device?.runtime?.interpretations?.length > 0);

    if (initialLoading) {
        return (
            <div
                className={`tw-border tw-rounded-lg tw-bg-white tw-w-full tw-flex tw-items-center tw-justify-center ${
                    isExpanded ? "tw-h-full" : ""
                }`}
                style={{ height: isExpanded ? containerHeight : "120px" }}
            >
                <Spinner />
            </div>
        );
    }

    return (
        <div
            className={`tw-border tw-rounded-lg tw-bg-white tw-w-full tw-relative ${
                isExpanded ? "tw-h-full tw-flex tw-flex-col" : ""
            }`}
        >
            {/* No interpretations configured at all - shown regardless of
                the "Physical signals" checkbox (it's not about whether
                anything is currently visible, it's that nothing business-
                friendly has been set up yet), so it lives above the
                toolbar rather than inside the channel-filtering logic. */}
            {isExpanded && hasNoInterpretations && (
                <div className="tw-flex tw-items-center tw-justify-between tw-gap-3 tw-px-4 tw-py-2 tw-border-b tw-bg-yellow-50 tw-text-yellow-800 tw-text-sm">
                    <span className="tw-flex tw-items-center tw-gap-2">
                        <i className="fa fa-exclamation-triangle" />
                        No interpretations configured for this device yet -
                        physical signals aren't business-friendly on their own.
                    </span>
                    {onOpenInterpretations && (
                        <button
                            type="button"
                            onClick={() => onOpenInterpretations(device)}
                            className="tw-shrink-0 tw-font-medium tw-text-yellow-900 hover:tw-underline"
                        >
                            + Create interpretation
                        </button>
                    )}
                </div>
            )}

            {/* Auto-refresh + show/hide controls - LiveView only */}
            {isExpanded && (
                <div className="tw-flex tw-items-stretch tw-justify-end tw-gap-3 tw-px-4 tw-py-2 tw-border-b tw-bg-gray-50">
                    <label className="tw-h-8 tw-flex tw-items-center tw-gap-1.5 tw-text-sm tw-text-gray-600 tw-cursor-pointer">
                        <input
                            type="checkbox"
                            checked={showPhysicalSignals}
                            onChange={(e) =>
                                setShowPhysicalSignals(e.target.checked)
                            }
                        />
                        Physical signals
                    </label>
                    <div className="tw-w-px tw-h-4 tw-self-center tw-bg-gray-300" />
                    <label className="tw-h-8 tw-flex tw-items-center tw-gap-1.5 tw-text-sm tw-text-gray-600 tw-cursor-pointer">
                        <input
                            type="checkbox"
                            checked={autoRefreshEnabled}
                            onChange={(e) => setAutoRefreshEnabled(e.target.checked)}
                        />
                        Auto-refresh
                    </label>
                    {autoRefreshEnabled ? (
                        <select
                            value={refreshIntervalSec}
                            onChange={(e) =>
                                setRefreshIntervalSec(Number(e.target.value))
                            }
                            className="tw-h-8 tw-w-40 tw-text-sm tw-border tw-rounded tw-px-2 tw-py-0 tw-self-center"
                        >
                            <option value={10}>Every 10 seconds</option>
                            <option value={30}>Every 30 seconds</option>
                            <option value={60}>Every 1 minute</option>
                            <option value={300}>Every 5 minutes</option>
                        </select>
                    ) : (
                        <button
                            onClick={() => fetchDeviceData(false)}
                            disabled={isRefreshing}
                            className="tw-h-8 tw-self-center tw-flex tw-items-center tw-gap-1.5 tw-text-sm tw-px-3 tw-bg-blue-500 tw-text-white tw-rounded hover:tw-bg-blue-600 disabled:tw-opacity-60"
                        >
                            <i
                                className={`fa fa-sync-alt ${
                                    isRefreshing ? "fa-spin" : ""
                                }`}
                            />
                            Refresh
                        </button>
                    )}
                </div>
            )}

            {/* Header with navigation and controls */}
            <DeviceMonitorControl
                currentSignalKey={currentSignalKey}
                signalTypeInfo={signalTypeInfo}
                isExpanded={isExpanded}
                currentSignalIndex={currentSignalIndex}
                totalSignals={signalKeys.length}
                onPrevSignal={prevSignal}
                onNextSignal={nextSignal}
                isRefreshing={isRefreshing}
                initialValue={initialControlValue}
                onValueChange={handleValueChange}
                isInverted={currentSignal?.inverted}
                isAuxiliary={currentSignal?.auxillary}
            />

            {/* Body: graph on the left, control widget (when applicable) in
                a right-hand column - not stacked, so the graph keeps a
                consistent width regardless of which signal is selected. */}
            <div
                className={
                    showControlColumn
                        ? "tw-w-full tw-flex-1 tw-min-h-0 tw-flex tw-items-stretch"
                        : isExpanded
                          ? "tw-w-full tw-flex-1 tw-min-h-0 tw-flex tw-flex-col"
                          : "tw-w-full"
                }
            >
                {/* Graph area */}
                <div className="tw-flex-1 tw-min-w-0 tw-min-h-0 tw-flex tw-flex-col">
                {isExpanded && !showControlColumn && (
                    <div className="tw-font-semibold tw-text-gray-800 tw-text-sm tw-text-center tw-pt-4">
                        {currentSignalKey}
                    </div>
                )}
                <div
                    className={`tw-flex-1 tw-min-w-0 tw-min-h-0 tw-flex tw-items-center tw-justify-center ${
                        isExpanded ? "tw-p-0" : "tw-pr-2"
                    }`}
                    style={isExpanded ? undefined : { height: containerHeight }}
                >
                    {noData ? (
                        <div
                            className={`tw-text-gray-500 ${
                                isExpanded ? "tw-text-xl" : "tw-text-sm"
                            }`}
                        >
                            Couldn't find any data
                        </div>
                    ) : (
                        <div className="tw-relative tw-w-full tw-h-full">
                            <DeviceGraphView
                                graphData={graphData}
                                signalColor={currentSignal?.color || "#0080ff"}
                                lineType={lineType}
                                isDigital={signalTypeInfo.digital}
                                isExpanded={isExpanded}
                                startTime={startTime}
                                endTime={endTime}
                                chartMargin={chartMargin}
                                CustomTooltip={CustomTooltip}
                                isAuxiliary={currentSignal?.auxillary}
                                isPhysical={currentSignal?.isPhysical}
                            />

                            <DeviceGraphStatus
                                isRefreshing={isRefreshing}
                                isExpanded={isExpanded}
                            />
                        </div>
                    )}
                </div>
                </div>

                {/* Control widget column - width reserved as soon as a
                    signal is selected (spinner while its type decodes), so
                    the graph's own width never jumps once the real widget
                    shows up. */}
                {showControlColumn && (
                    <div className="tw-w-64 tw-shrink-0 tw-border-l tw-flex tw-flex-col tw-items-center tw-justify-center tw-p-6">
                        <div className="tw-font-semibold tw-text-gray-800 tw-text-sm tw-mb-4 tw-text-center tw-break-all">
                            {currentSignalKey}
                        </div>
                        {signalTypeInfoLoading ? (
                            <Spinner />
                        ) : (
                            <DeviceMonitorControlWidget
                                key={currentSignalKey}
                                signalTypeInfo={signalTypeInfo}
                                isExpanded={isExpanded}
                                initialValue={initialControlValue}
                                onValueChange={handleValueChange}
                                inverted={!!currentSignal?.inverted}
                                isInterpretation={!currentSignal?.isPhysical}
                            />
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

// Memoize component to prevent re-renders when props haven't changed
export default memo(DeviceGraph, (prevProps, nextProps) => {
    // Only re-render if device.id or isExpanded changes
    return (
        prevProps.device.id === nextProps.device.id &&
        prevProps.isExpanded === nextProps.isExpanded &&
        prevProps.sendDeviceCommand === nextProps.sendDeviceCommand
    );
});
